import { useState, useEffect, useRef, type RefObject } from 'react';

type Task = {
  id: number;
  name: string;
  phase_name: string;
  start_date: string | null;
  end_date: string | null;
  status: string;
  assignee_name: string | null;
  depends_on_ids: number[];
};

type Props = {
  tasks: Task[];
  onUpdateDates: (taskId: number, startDate: string, endDate: string) => void;
  scrollRef?: RefObject<HTMLDivElement | null>;
};

type ViewMode = 'Week' | 'Month' | 'Quarter' | 'Year';

type DragState = {
  taskId: number;
  type: 'move' | 'resize-left' | 'resize-right';
  startX: number;
  originalStart: string;
  originalEnd: string;
};

const addDays = (dateStr: string, days: number): string => {
  if (!isFinite(days)) return dateStr.substring(0, 10);
  const [y, m, d] = dateStr.substring(0, 10).split('-').map(Number);
  const date = new Date(Date.UTC(y, m - 1, d));
  date.setUTCDate(date.getUTCDate() + days);
  const yr = date.getUTCFullYear();
  const mo = String(date.getUTCMonth() + 1).padStart(2, '0');
  const dy = String(date.getUTCDate()).padStart(2, '0');
  return `${yr}-${mo}-${dy}`;
};

export default function GanttChart({ tasks, onUpdateDates, scrollRef }: Props) {
  const [viewMode, setViewMode] = useState<ViewMode>('Month');
  const [dragging, setDragging] = useState<DragState | null>(null);
  const [dragPos, setDragPos] = useState<{ start: string; end: string } | null>(null);
  const dragPosRef = useRef<{ start: string; end: string } | null>(null);

  const tasksWithDates = tasks.filter(t => t.start_date && t.end_date);

  const allStarts = tasksWithDates.map(t => new Date(t.start_date!).getTime());
  const allEnds = tasksWithDates.map(t => new Date(t.end_date!).getTime());
  const minDate = tasksWithDates.length > 0 ? new Date(Math.min(...allStarts)) : new Date();
  const maxDate = tasksWithDates.length > 0 ? new Date(Math.max(...allEnds)) : new Date();
  minDate.setDate(minDate.getDate() - 3);
  maxDate.setDate(maxDate.getDate() + 3);

  const totalDays = Math.ceil((maxDate.getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24));
  const dayWidth = viewMode === 'Week' ? 40 : viewMode === 'Month' ? 20 : viewMode === 'Quarter' ? 8 : 3;
  const totalWidth = totalDays * dayWidth;
  const LABEL_WIDTH = 192;

  const getLeft = (date: string) => {
    const days = Math.ceil((new Date(date).getTime() - minDate.getTime()) / (1000 * 60 * 60 * 24));
    return days * dayWidth;
  };

  const getWidth = (start: string, end: string) => {
    const days = Math.ceil((new Date(end).getTime() - new Date(start).getTime()) / (1000 * 60 * 60 * 24)) + 1;
    return Math.max(days * dayWidth, 4);
  };

  const statusColor = (status: string) => {
    if (status === 'Complete') return 'bg-green-400';
    if (status === 'In Progress') return 'bg-blue-400';
    if (status === 'Blocked') return 'bg-red-400';
    return 'bg-primary';
  };

  const formatDate = (date: string) => new Date(date).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric', year: 'numeric' });
  const formatShort = (date: string) => new Date(date).toLocaleDateString('en-US', { timeZone: 'UTC', month: 'short', day: 'numeric' });

  // Drag handling
  useEffect(() => {
    if (!dragging) return;

    const handleMouseMove = (e: MouseEvent) => {
      const deltaPx = e.clientX - dragging.startX;
      const deltaDays = Math.round(deltaPx / dayWidth);
      if (!isFinite(deltaDays)) return;

      let newStart = dragging.originalStart;
      let newEnd = dragging.originalEnd;

      if (dragging.type === 'move') {
        newStart = addDays(dragging.originalStart, deltaDays);
        newEnd = addDays(dragging.originalEnd, deltaDays);
      } else if (dragging.type === 'resize-left') {
        newStart = addDays(dragging.originalStart, deltaDays);
        if (newStart >= dragging.originalEnd) newStart = addDays(dragging.originalEnd, -1);
      } else if (dragging.type === 'resize-right') {
        newEnd = addDays(dragging.originalEnd, deltaDays);
        if (newEnd <= dragging.originalStart) newEnd = addDays(dragging.originalStart, 1);
      }

      const pos = { start: newStart, end: newEnd };
      setDragPos(pos);
      dragPosRef.current = pos;
    };

    const handleMouseUp = () => {
      if (dragPosRef.current) {
        onUpdateDates(dragging.taskId, dragPosRef.current.start, dragPosRef.current.end);
      }
      setDragging(null);
      setDragPos(null);
      dragPosRef.current = null;
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, dayWidth, onUpdateDates]);

  // Prevent text selection while dragging
  useEffect(() => {
    if (dragging) {
      document.body.style.userSelect = 'none';
    } else {
      document.body.style.userSelect = '';
    }
  }, [dragging]);

  const markers: { label: string; left: number }[] = [];
  const cursor = new Date(minDate);

  if (viewMode === 'Week') {
    while (cursor <= maxDate) {
      markers.push({ label: formatShort(cursor.toISOString().split('T')[0]), left: getLeft(cursor.toISOString().split('T')[0]) });
      cursor.setDate(cursor.getDate() + 7);
    }
  } else if (viewMode === 'Month') {
    cursor.setDate(1);
    while (cursor <= maxDate) {
      markers.push({ label: cursor.toLocaleDateString('en-US', { month: 'short', year: 'numeric' }), left: getLeft(cursor.toISOString().split('T')[0]) });
      cursor.setMonth(cursor.getMonth() + 1);
    }
  } else if (viewMode === 'Quarter') {
    cursor.setDate(1);
    cursor.setMonth(Math.floor(cursor.getMonth() / 3) * 3);
    while (cursor <= maxDate) {
      const q = Math.floor(cursor.getMonth() / 3) + 1;
      markers.push({ label: `Q${q} ${cursor.getFullYear()}`, left: getLeft(cursor.toISOString().split('T')[0]) });
      cursor.setMonth(cursor.getMonth() + 3);
    }
  } else {
    cursor.setDate(1);
    cursor.setMonth(0);
    while (cursor <= maxDate) {
      markers.push({ label: String(cursor.getFullYear()), left: getLeft(cursor.toISOString().split('T')[0]) });
      cursor.setFullYear(cursor.getFullYear() + 1);
    }
  }

  const phases = Array.from(new Set(tasksWithDates.map(t => t.phase_name)));

  // Compute y-center positions for each task row (for dependency arrows)
  const DATE_HEADER_H = 28;   // h-6(24) + mb-1(4)
  const PHASE_LABEL_H = 20;   // text-xs(16) + mb-1(4)
  const PHASE_BOTTOM_H = 16;  // mb-4
  const TASK_ROW_H = 36;      // h-8(32) + mb-1(4)
  const taskYCenter = new Map<number, number>();
  let yAccum = DATE_HEADER_H;
  for (const phase of phases) {
    yAccum += PHASE_LABEL_H;
    for (const task of tasksWithDates.filter(t => t.phase_name === phase)) {
      taskYCenter.set(task.id, yAccum + 16);
      yAccum += TASK_ROW_H;
    }
    yAccum += PHASE_BOTTOM_H;
  }
  const svgHeight = yAccum;

  if (tasksWithDates.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow p-10 text-center">
        <p className="text-gray-400 text-lg mb-2">No tasks with dates yet</p>
        <p className="text-gray-300 text-sm">Set start and end dates on your tasks to see them on the Gantt chart.</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow p-6">
      <div className="flex gap-2 mb-4">
        {(['Week', 'Month', 'Quarter', 'Year'] as ViewMode[]).map(mode => (
          <button
            key={mode}
            onClick={() => setViewMode(mode)}
            className={`px-4 py-1 rounded-full text-sm font-semibold transition-colors ${viewMode === mode ? 'bg-primary text-white' : 'bg-gray-100 text-gray-500 hover:bg-gray-200'}`}>
            {mode}
          </button>
        ))}
      </div>

      <div ref={scrollRef} data-gantt-scroll="true" style={{ maxHeight: '60vh', overflowY: 'auto', overflowX: 'auto' }}>
        <div data-gantt-inner="true" style={{ minWidth: `${LABEL_WIDTH + totalWidth}px`, position: 'relative' }}>

          <div className="flex mb-1">
            <div style={{ width: LABEL_WIDTH }} className="flex-shrink-0" />
            <div className="relative h-6" style={{ width: totalWidth }}>
              {markers.map((m, i) => (
                <div key={i} className="absolute text-xs text-gray-400 whitespace-nowrap" style={{ left: `${m.left}px` }}>
                  {m.label}
                </div>
              ))}
            </div>
          </div>

          {phases.map(phase => (
            <div key={phase} className="mb-4">
              <div className="text-xs font-bold text-primary uppercase tracking-wide mb-1 pl-1">{phase}</div>
              {tasksWithDates.filter(t => t.phase_name === phase).map((task, taskIndex) => {
                const isDraggingThis = dragging?.taskId === task.id;
                const effectiveStart = isDraggingThis && dragPos ? dragPos.start : task.start_date!;
                const effectiveEnd = isDraggingThis && dragPos ? dragPos.end : task.end_date!;

                return (
                  <div key={task.id} className={`flex items-center mb-1 h-8 rounded ${taskIndex % 2 === 0 ? 'bg-white' : 'bg-gray-100'}`}>
                    <div style={{ width: LABEL_WIDTH }} className="flex-shrink-0 pr-3 pl-1">
                      <div className="text-xs font-medium text-gray-700 truncate">{task.name}</div>
                      {task.assignee_name && <div className="text-xs text-gray-400 truncate">{task.assignee_name}</div>}
                    </div>
                    <div className="relative h-7 rounded" style={{ width: totalWidth }}>
                      {markers.map((m, i) => (
                        <div key={i} className="absolute top-0 bottom-0 border-l border-gray-200" style={{ left: `${m.left}px` }} />
                      ))}
                      <div
                        className={`absolute h-full rounded ${statusColor(task.status)} opacity-90 flex items-center group ${isDraggingThis ? 'cursor-grabbing opacity-100' : 'cursor-grab'}`}
                        style={{ left: `${getLeft(effectiveStart)}px`, width: `${getWidth(effectiveStart, effectiveEnd)}px` }}
                        onMouseDown={(e) => {
                          e.preventDefault();
                          setDragging({ taskId: task.id, type: 'move', startX: e.clientX, originalStart: task.start_date!, originalEnd: task.end_date! });
                        }}
                      >
                        {/* Left resize handle */}
                        <div
                          className="absolute left-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white hover:bg-opacity-30 rounded-l"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDragging({ taskId: task.id, type: 'resize-left', startX: e.clientX, originalStart: task.start_date!, originalEnd: task.end_date! });
                          }}
                        />
                        <span className="text-white text-xs whitespace-nowrap px-3 select-none" style={{ textShadow: '0 0 3px #000, 0 0 5px #000, 0 0 8px #000, 1px 1px 2px #000, -1px -1px 2px #000' }}>
                          {formatShort(effectiveStart)} — {formatShort(effectiveEnd)}
                        </span>
                        {/* Right resize handle */}
                        <div
                          className="absolute right-0 top-0 bottom-0 w-2 cursor-ew-resize hover:bg-white hover:bg-opacity-30 rounded-r"
                          onMouseDown={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            setDragging({ taskId: task.id, type: 'resize-right', startX: e.clientX, originalStart: task.start_date!, originalEnd: task.end_date! });
                          }}
                        />
                        {/* Tooltip — only when not dragging */}
                        {!isDraggingThis && (
                          <div className="absolute bottom-full left-0 mb-2 hidden group-hover:block z-50 pointer-events-none">
                            <div className="bg-gray-800 text-white text-xs rounded-lg px-3 py-2 whitespace-nowrap shadow-lg">
                              <div className="font-semibold mb-1">{task.name}</div>
                              <div>{formatDate(task.start_date!)} — {formatDate(task.end_date!)}</div>
                              {task.assignee_name && <div className="text-gray-300 mt-1">👤 {task.assignee_name}</div>}
                              <div className="text-gray-300">{task.status}</div>
                            </div>
                            <div className="w-2 h-2 bg-gray-800 rotate-45 ml-3 -mt-1"></div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          ))}

        {/* Dependency arrows */}
        {(() => {
          const arrows: { x1: number; y1: number; x2: number; y2: number }[] = [];
          for (const task of tasksWithDates) {
            if (!task.depends_on_ids?.length) continue;
            const y2 = taskYCenter.get(task.id);
            const x2 = LABEL_WIDTH + getLeft(task.start_date!);
            if (y2 == null) continue;
            for (const predId of task.depends_on_ids) {
              const pred = tasksWithDates.find(t => t.id === predId);
              if (!pred) continue;
              const y1 = taskYCenter.get(predId);
              if (y1 == null) continue;
              const x1 = LABEL_WIDTH + getLeft(pred.start_date!) + getWidth(pred.start_date!, pred.end_date!);
              arrows.push({ x1, y1, x2, y2 });
            }
          }
          if (arrows.length === 0) return null;
          return (
            <svg
              style={{ position: 'absolute', top: 0, left: 0, pointerEvents: 'none', overflow: 'visible' }}
              width={LABEL_WIDTH + totalWidth}
              height={svgHeight}
            >
              <defs>
                <marker id="arrowhead" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                  <path d="M0,0 L0,6 L6,3 z" fill="#475569" />
                </marker>
              </defs>
              {arrows.map((a, i) => {
                const dx = Math.abs(a.x2 - a.x1);
                const cx1 = a.x1 + Math.max(dx * 0.4, 20);
                const cx2 = a.x2 - Math.max(dx * 0.4, 20);
                return (
                  <path
                    key={i}
                    d={`M ${a.x1} ${a.y1} C ${cx1} ${a.y1} ${cx2} ${a.y2} ${a.x2} ${a.y2}`}
                    fill="none"
                    stroke="#475569"
                    strokeWidth="1.5"
                    strokeDasharray="4 3"
                    markerEnd="url(#arrowhead)"
                  />
                );
              })}
            </svg>
          );
        })()}

        </div>
      </div>

      <div className="flex gap-4 mt-4 pt-4 border-t border-gray-100">
        <span className="flex items-center gap-1 text-xs text-gray-500"><span className="w-3 h-3 rounded-sm bg-primary inline-block"></span>Not Started</span>
        <span className="flex items-center gap-1 text-xs text-gray-500"><span className="w-3 h-3 rounded-sm bg-blue-400 inline-block"></span>In Progress</span>
        <span className="flex items-center gap-1 text-xs text-gray-500"><span className="w-3 h-3 rounded-sm bg-green-400 inline-block"></span>Complete</span>
        <span className="flex items-center gap-1 text-xs text-gray-500"><span className="w-3 h-3 rounded-sm bg-red-400 inline-block"></span>Blocked</span>
      </div>
    </div>
  );
}
